import { advancedThreadForItem, lastAnsweredJobOfThread, type V2AiJob } from '../domain/aiJob';
import { observationBelongsToRow, observationIsUnclassified, type V2AiObservation } from '../domain/aiObservation';
import { templateRowScope } from '../domain/checklistTemplates';
import type { V2Area, V2ChecklistItem } from '../domain/inspection';
import { photoShotsFor } from '../domain/photoGuide';
import type { AiState } from './aiStore';
import type { EvidenceState } from './evidenceStore';
import type { FindingState } from './findingStore';
import type { InspectionState } from './inspectionStore';
import { areaScanPhotos } from './photoPool';

// 离开前再看一眼 · Before you leave (2026-10-27; docs/qa/evidence/ai-overnight-2026-10-26 README +
// workflow-review.md §三 / §四 A2 + B3). Scoring the AI against five finished reports left three kinds of
// misses that no prompt or model change fixed: rows with no photo (row coverage 78 %), defects at the edge
// of distant photos, and absences that need the whole area (no smoke / CO alarm on a level). They are caught
// best while the inspector is still in the house. This is an ADVISORY list on the closeout screen — it never
// blocks the report, it writes nothing, and every line opens the row (or the area) it is about.
//
//   aiUncertain  a row the AI could not settle — 「需现场测试」 (fieldTestRequired), 「看不清」 (unclear or a
//                clarification question), or a LOW-confidence conclusion — and the inspector's result leaves
//                it open: no result yet; or 无异常 without the test recorded (fieldTest) / without a word of
//                what was seen (unclear, low-confidence defect). A defect, N/A or Unable result, or 无异常 that
//                agrees with a low-confidence "no visible defect", settles it.
//   aiQuestion   a question the AI asked that nobody answered: an unclassified clarification still open, or
//                the advanced review's 「需要补拍」 with no photo added to the row since the answer came.
//   aiUnfiled    the area's other AI findings still waiting in its unclassified list (one line, a count).
//   aiNewPhotos  photos of an area the AI already analyzed that it has not seen since (one line, a count) —
//                only for an area the inspector analyzed at least once and did not mark 「不用 AI」.
//   alarmLevel   a level (a floor, or the basement) whose smoke / CO alarm rows (int-alarms / bed-alarm /
//                bsmt-alarm) carry no 无异常 or 缺陷 — "is there an alarm on this level" was never answered.
//   noPhoto      a 缺陷 row with no photo on it, or a 无异常 row with no photo whose shot the area's photo
//                checklist asks for (2026-10-28: every 无异常 row was too many to read). Not listed: N/A and Unable (nothing was
//                observed there to photograph); a method / limitation row the photo checklist has no shot for
//                (photos are not expected there); a row whose photo-checklist shots were all marked 「没有 /
//                不需要」; a row whose photo-checklist shot was taken at area level; the whole-house safety
//                review rows (they review rows that carry their own photos in other areas).
//
// The AI kinds come only from AI records, so an inspector who never uses AI sees only alarmLevel and noPhoto.
export type PreDepartureKind = 'aiUncertain' | 'aiQuestion' | 'aiUnfiled' | 'aiNewPhotos' | 'alarmLevel' | 'noPhoto';

// Why an AI line is there (aiUncertain): the AI's own conclusion, strongest first.
export type AiUncertainReason = 'fieldTest' | 'unclear' | 'lowDefect' | 'lowNoDefect';

export type PreDepartureLevel = { kind: 'floor'; floor: number } | { kind: 'basement' };

export interface PreDepartureLine {
  kind: PreDepartureKind;
  // the line comes from an AI record
  ai: boolean;
  // the area the line opens (alarmLevel: the area of the level's first alarm row)
  areaId: string;
  // the row the line opens, or null for an area-level line (aiUnfiled, aiNewPhotos, an unclassified question)
  itemId: string | null;
  // aiUncertain: what the AI concluded
  reason: AiUncertainReason | null;
  // aiUncertain: the row has no result yet (otherwise it is 无异常)
  rowOpen: boolean;
  // noPhoto: the row is a 缺陷
  defect: boolean;
  // aiQuestion / aiUncertain: the question as the AI wrote it ('' never); aiQuestion: the subject when no row
  question: string | null;
  title: string | null;
  // aiQuestion: who asked
  source: 'observation' | 'advanced' | null;
  // aiUnfiled / aiNewPhotos
  count: number;
  // alarmLevel
  level: PreDepartureLevel | null;
}

export interface PreDepartureGroup {
  areaId: string;
  lines: PreDepartureLine[];
}

export interface PreDepartureCheck {
  // by area, in walk order; inside an area the AI lines first, then alarms, then rows without a photo
  groups: PreDepartureGroup[];
  lines: PreDepartureLine[];
  total: number;
  aiLines: number;
}

export interface PreDepartureInput {
  inspection: InspectionState;
  ai?: Pick<AiState, 'observations' | 'runs'> | null;
  jobs?: readonly V2AiJob[] | null;
  evidence?: EvidenceState | null;
  findings?: FindingState | null;
}

// The rows that say whether a level has its smoke / CO alarm (src/domain/checklistTemplates.ts).
export const ALARM_TEMPLATE_IDS: readonly string[] = Object.freeze(['int-alarms', 'bed-alarm', 'bsmt-alarm']);

const KIND_ORDER: Record<PreDepartureKind, number> = { aiUncertain: 0, aiQuestion: 1, alarmLevel: 2, aiUnfiled: 3, aiNewPhotos: 4, noPhoto: 5 };
const REASON_ORDER: Record<AiUncertainReason, number> = { fieldTest: 0, unclear: 1, lowDefect: 2, lowNoDefect: 3 };

function line(kind: PreDepartureKind, areaId: string, itemId: string | null, extra: Partial<PreDepartureLine> = {}): PreDepartureLine {
  return {
    kind,
    ai: kind === 'aiUncertain' || kind === 'aiQuestion' || kind === 'aiUnfiled' || kind === 'aiNewPhotos',
    areaId,
    itemId,
    reason: null,
    rowOpen: false,
    defect: false,
    question: null,
    title: null,
    source: null,
    count: 0,
    level: null,
    ...extra
  };
}

// What the AI concluded on a row, when a photograph could not settle it; null for a firm conclusion.
export function aiUncertainReasonOf(observation: Pick<V2AiObservation, 'verdict' | 'confidence' | 'type'>): AiUncertainReason | null {
  if (observation.verdict === 'fieldTestRequired') return 'fieldTest';
  if (observation.verdict === 'unclear' || observation.type === 'needsClarification') return 'unclear';
  if (observation.confidence !== 'low') return null;
  if (observation.verdict === 'defect') return 'lowDefect';
  if (observation.verdict === 'noVisibleDefect') return 'lowNoDefect';
  return 'unclear';
}

// Whether the inspector's result on the row leaves that conclusion open (see the header).
export function rowLeavesAiOpen(item: Pick<V2ChecklistItem, 'status' | 'inspectorNote' | 'operationalTestPerformed'>, reason: AiUncertainReason): boolean {
  if (item.status === 'notChecked') return true;
  if (item.status !== 'checkedOk') return false;
  const noted = item.inspectorNote.trim().length > 0;
  if (reason === 'fieldTest') return !item.operationalTestPerformed && !noted;
  if (reason === 'lowNoDefect') return false;
  return !noted;
}

function levelOf(area: V2Area): { key: string; level: PreDepartureLevel } | null {
  if (area.floor !== null) return { key: `floor:${area.floor}`, level: { kind: 'floor', floor: area.floor } };
  if (area.kind === 'basement') return { key: 'basement', level: { kind: 'basement' } };
  return null;
}

// A method / limitation row the photo checklist has no shot for: no photo is expected there.
function photoNotExpected(area: V2Area, templateId: string | null): boolean {
  if (templateId === null) return false;
  return templateRowScope(templateId) === 'methodLimitation' && !photoShotsFor(area.kind).some((s) => s.templateId === templateId);
}

// The area's photo checklist has a shot for this row: a photo is expected there even when the row is fine.
function photoShotExpected(area: V2Area, templateId: string | null): boolean {
  if (templateId === null) return false;
  return photoShotsFor(area.kind).some((s) => s.templateId === templateId);
}

// Every photo-checklist shot of this row was answered 「没有 / 不需要」.
function shotsAllSkipped(area: V2Area, templateId: string | null): boolean {
  if (templateId === null) return false;
  const shots = photoShotsFor(area.kind).filter((s) => s.templateId === templateId);
  const skips = new Set(area.shotSkips ?? []);
  return shots.length > 0 && shots.every((s) => skips.has(s.key));
}

export function buildPreDepartureCheck(inspectionId: string, input: PreDepartureInput): PreDepartureCheck {
  const areas = input.inspection.areas.filter((a) => a.inspectionId === inspectionId && a.enabled).sort((a, b) => a.sortOrder - b.sortOrder);
  const areaById = new Map(areas.map((a) => [a.id, a] as const));
  const items = input.inspection.items.filter((i) => i.inspectionId === inspectionId && areaById.has(i.areaId)).sort((a, b) => a.sortOrder - b.sortOrder);
  const observations = (input.ai?.observations ?? []).filter((o) => o.inspectionId === inspectionId && areaById.has(o.areaId));
  const runs = (input.ai?.runs ?? []).filter((r) => r.inspectionId === inspectionId);
  const jobs = (input.jobs ?? []).filter((j) => j.inspectionId === inspectionId);
  const evidence = input.evidence?.evidence ?? [];
  const evidenceById = new Map(evidence.map((e) => [e.id, e] as const));
  const lines: PreDepartureLine[] = [];

  const rowObservations = new Map<string, V2AiObservation[]>();
  for (const o of observations) {
    if (!observationBelongsToRow(o) || o.itemId === null) continue;
    rowObservations.set(o.itemId, [...(rowObservations.get(o.itemId) ?? []), o]);
  }

  // The template rows a photo-checklist shot taken at area level documents (the shot names its row).
  const shotRows = new Map<string, Set<string>>();
  for (const e of evidence) {
    if (!e.areaId || !e.shot) continue;
    const area = areaById.get(e.areaId);
    const shot = area ? photoShotsFor(area.kind).find((s) => s.key === e.shot) : undefined;
    if (!area || !shot || !shot.templateId) continue;
    const set = shotRows.get(area.id) ?? new Set<string>();
    set.add(shot.templateId);
    shotRows.set(area.id, set);
  }

  for (const item of items) {
    const area = areaById.get(item.areaId) as V2Area;

    // aiUncertain — one line per row, the strongest open conclusion
    let picked: { reason: AiUncertainReason; observation: V2AiObservation } | null = null;
    for (const o of rowObservations.get(item.id) ?? []) {
      const reason = aiUncertainReasonOf(o);
      if (reason === null || !rowLeavesAiOpen(item, reason)) continue;
      if (!picked || REASON_ORDER[reason] < REASON_ORDER[picked.reason]) picked = { reason, observation: o };
    }
    if (picked) {
      const question = (picked.observation.question ?? '').trim();
      lines.push(line('aiUncertain', area.id, item.id, { reason: picked.reason, rowOpen: item.status === 'notChecked', question: question.length > 0 ? question : null }));
    }

    // aiQuestion — the advanced review asked for one more photo and none was added since
    if (item.status !== 'notApplicable' && jobs.length > 0) {
      const last = lastAnsweredJobOfThread(advancedThreadForItem(jobs, item.id));
      const question = (last?.result?.question ?? '').trim();
      if (last && question.length > 0) {
        const answeredAt = last.completedAt ?? last.updatedAt;
        const photoSince = item.evidenceIds.some((id) => (evidenceById.get(id)?.createdAt ?? 0) > answeredAt);
        if (!photoSince) lines.push(line('aiQuestion', area.id, item.id, { question, source: 'advanced' }));
      }
    }

    // noPhoto. TestFlight 210 on the 85-photo house (2026-10-28): listing every 无异常 row without a photo put
    // ~37 of 53 lines here, and a NY inspection does not photograph rows that are fine — the list stopped
    // being read. A 缺陷 row is listed whenever it has no photo (a defect needs its evidence); a 无异常 row
    // only when this area's photo checklist asks for a shot of that row.
    const listedForPhoto = item.status === 'issueFound' || (item.status === 'checkedOk' && photoShotExpected(area, item.templateId));
    if (listedForPhoto && item.evidenceIds.length === 0 && area.kind !== 'safety') {
      const shotTaken = item.templateId !== null && (shotRows.get(area.id)?.has(item.templateId) ?? false);
      if (!shotTaken && !photoNotExpected(area, item.templateId) && !shotsAllSkipped(area, item.templateId)) {
        lines.push(line('noPhoto', area.id, item.id, { defect: item.status === 'issueFound' }));
      }
    }
  }

  for (const area of areas) {
    // aiQuestion / aiUnfiled — the area's unclassified list
    const open = observations.filter((o) => o.areaId === area.id && observationIsUnclassified(o)).sort((a, b) => a.createdAt - b.createdAt || a.sortOrder - b.sortOrder);
    let unfiled = 0;
    for (const o of open) {
      const question = (o.question ?? '').trim();
      if (o.type === 'needsClarification' && question.length > 0) lines.push(line('aiQuestion', area.id, null, { question, title: o.title.trim() || null, source: 'observation' }));
      else unfiled += 1;
    }
    if (unfiled > 0) lines.push(line('aiUnfiled', area.id, null, { count: unfiled }));

    // aiNewPhotos — only where the inspector already analyzed this area
    if (!area.aiSkipped && input.evidence && input.findings) {
      const ofArea = runs.filter((r) => r.areaId === area.id);
      if (ofArea.some((r) => r.status === 'done') && !ofArea.some((r) => r.status === 'running')) {
        const analyzed = new Set(ofArea.filter((r) => r.status === 'done').flatMap((r) => r.photoIds));
        const fresh = areaScanPhotos(input.evidence, input.inspection, input.findings, area.id).filter((e) => !analyzed.has(e.id)).length;
        if (fresh > 0) lines.push(line('aiNewPhotos', area.id, null, { count: fresh }));
      }
    }
  }

  // alarmLevel — per level, in walk order
  const levels = new Map<string, { level: PreDepartureLevel; rows: V2ChecklistItem[] }>();
  for (const area of areas) {
    const at = levelOf(area);
    if (!at) continue;
    for (const item of items) {
      if (item.areaId !== area.id || item.templateId === null || !ALARM_TEMPLATE_IDS.includes(item.templateId)) continue;
      const entry = levels.get(at.key) ?? { level: at.level, rows: [] };
      entry.rows.push(item);
      levels.set(at.key, entry);
    }
  }
  for (const { level, rows } of levels.values()) {
    if (rows.some((r) => r.status === 'checkedOk' || r.status === 'issueFound')) continue;
    const first = rows[0];
    lines.push(line('alarmLevel', first.areaId, first.id, { level }));
  }

  const order = new Map(lines.map((l, i) => [l, i] as const));
  const groups: PreDepartureGroup[] = [];
  for (const area of areas) {
    const ofArea = lines
      .filter((l) => l.areaId === area.id)
      .sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || Number(b.defect) - Number(a.defect) || (order.get(a) ?? 0) - (order.get(b) ?? 0));
    if (ofArea.length > 0) groups.push({ areaId: area.id, lines: ofArea });
  }
  const flat = groups.flatMap((g) => g.lines);
  return { groups, lines: flat, total: flat.length, aiLines: flat.filter((l) => l.ai).length };
}
